import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { createDocument, isDocIdTaken } from '@/data/util_documentIds';
import { db } from '@/firebaseInit';
import { normaliseShortCode } from '@/types/types_code';
import { coll_partners, normalisePartner, type PartnerType } from '@/types/types_partner';

// Data-access layer for partners, backed by Firestore. Mirrors the shape
// of experiencesRepo.ts so the autosave/history stack works the same way.

const partnersCollection = collection(db, coll_partners);

function toPartner(id: string, data: Record<string, unknown>): PartnerType {
  return normalisePartner(data as Partial<PartnerType>, id);
}

export async function listPartners(): Promise<PartnerType[]> {
  const snap = await getDocs(partnersCollection);
  return snap.docs.map((d) => toPartner(d.id, d.data()));
}

/**
 * Whether some *other* partner already holds this code.
 *
 * A one-shot query rather than a rule, for the same reason as
 * `isExperienceCodeTaken`: Firestore cannot express uniqueness across documents,
 * so this is a check the admin form runs, not a guarantee the database keeps.
 * Two admins minting the same code in the same second would both succeed.
 *
 * Admin-only in practice — a partner may not read another partner's record, so
 * this query is refused for anyone else.
 */
export async function isPartnerCodeTaken(code: string, excludeId?: string): Promise<boolean> {
  const normalised = normaliseShortCode(code);
  if (!normalised) return false;
  const snap = await getDocs(query(partnersCollection, where('code', '==', normalised)));
  return snap.docs.some((d) => d.id !== excludeId);
}

export async function getPartner(id: string): Promise<PartnerType | undefined> {
  const snap = await getDoc(doc(partnersCollection, id));
  return snap.exists() ? toPartner(snap.id, snap.data()) : undefined;
}

/**
 * Subscribes to all partners in real time. Mirrors Firestore's onSnapshot: the
 * initial value and every change after. Returns an unsubscribe function.
 */
export function subscribePartners(
  onChange: (partners: PartnerType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    partnersCollection,
    (snap) => onChange(snap.docs.map((d) => toPartner(d.id, d.data()))),
    onError,
  );
}

/**
 * Whether a partner already exists under this id. For a form to show while
 * somebody types; `createPartner` is what keeps the id unique.
 */
export async function isPartnerIdTaken(id: string): Promise<boolean> {
  return isDocIdTaken(partnersCollection, id);
}

/**
 * Creates a partner, refusing an id another partner already holds.
 *
 * Editing goes through `updatePartnerFields` (the form autosaves per field), so
 * nothing here needs to double as an update.
 */
export async function createPartner(partner: PartnerType): Promise<PartnerType> {
  return createDocument(partnersCollection, partner);
}

/**
 * Field-level update of a single partner, used by autosave. `updateObj` may
 * contain dotted field paths (e.g. history.*). `updatedAt` is stamped here.
 */
export async function updatePartnerFields(id: string, updateObj: Record<string, unknown>): Promise<void> {
  await updateDoc(doc(partnersCollection, id), {
    ...updateObj,
    updatedAt: new Date().toISOString(),
  });
}

/** Deletes a partner. */
export async function deletePartner(id: string): Promise<void> {
  await deleteDoc(doc(partnersCollection, id));
}
